import { ForecastWindData, ImpactSurfaceResponse } from "../../services/riskSurface";
import { SurfaceState } from "./surfaceSlice";
import Bowser from "bowser";
import { ForecastTime } from "../../app/forecastSurface";
import { snapInterpolation } from "../../app/utils";

// Utilities for helping with windowing the surface taus
// Note that the following is intentionally slightly different than isMobile from the pageWidthSlice (which considers
// screen width), the following only considers browser is mobile or is desktop as we don't want to change the WINDOW_SIZE
// variably with the page width.
const parser = Bowser.getParser( navigator.userAgent );
// shamelessly stolen from this thread: https://stackoverflow.com/questions/9038625/detect-if-device-is-ios
function iOS()
{
    return [
        "iPad Simulator",
        "iPhone Simulator",
        "iPod Simulator",
        "iPad",
        "iPhone",
        "iPod"
    ].includes( navigator.userAgent ) ||
  // iPad on iOS 13 detection
  ( navigator.userAgent.includes( "Mac" ) && "ontouchend" in document );
}
export const CAN_LOAD_ALL_TAUS = parser.getPlatformType().toLowerCase() === "desktop" && !iOS();
export const WINDOW_SIZE = 100;

export const isBrowserSafariMobile = () =>
{
    return parser.getBrowser().name?.toLowerCase() === "safari" &&
     ( parser.getPlatformType().toLowerCase() === "mobile" ||
      parser.getOS().name?.toLowerCase() === "ios" );
};

/**
 * Window of cached surface taus
 */
export interface ImpactWindow
{
    forecastTime: ForecastTime | undefined; // the time of the forecast being displayed (when it was generated)
    cachedEntries: Array<ImpactSurfaceResponse>; // list of loaded/cached surface taus
    pendingQueries: Array<IndexWithTimestamp>; // list of tau indexes that have been requested from the backend, but haven't been received
}

export interface WindDirectionWindow
{
    forecastTime: ForecastTime | undefined;
    taus: Array<ForecastWindData>;
}
interface IndexWithTimestamp
{
    index: number;
    timestamp: number;
}

/**
 * Sort ImpactSurfaceResponses according to their index into the geojson tau array
 * @param a first element to compare
 * @param b second element to compare
 */
function compare( a: ImpactSurfaceResponse, b: ImpactSurfaceResponse ): number
{
    return a.index - b.index;
}

/**
 * We've received an impact surface tau from the backend.  Add it to the cache and remove it from the pending queries list
 * @param state the redux state - necessary to ensure redux can detect changes to the state
 * @param dataToAdd the impact surface tau data from the backend
 */
export function push( state: SurfaceState, dataToAdd: Array<ImpactSurfaceResponse> ): boolean
{
    const indexesToAdd = dataToAdd.map( each => each.index );
    state.window.pendingQueries = state.window.pendingQueries
        .filter( each => !indexesToAdd.includes( each.index ) );
    state.window.cachedEntries = dataToAdd;
    state.window.cachedEntries.sort( compare );
    return true;
}

/**
 * We've made a request to the backend for a surface data tau(s), so add it to the pending queries list until we receive the response
 * @param state the redux state - necessary to ensure redux can detect changes to the state
 * @param indexes the index(es) of tau(s) that were requested from the backend
 */
export function addPendingToWindow( state: SurfaceState, indexes: Array<IndexWithTimestamp> ): boolean
{
    // It is possible that once we receive data, it's already no longer relevant because the slider has moved
    // upon making a request, we should add a placeholder for pending requests  - we would have to remove old/failed requests
    // and replace requests with incoming data if still relevant
    if ( indexes.length === 0 ) return false;

    const results = indexes.map( index =>
    {
        if ( state.window.pendingQueries.some( query => query.index === index.index ) ||
            state.window.cachedEntries.filter( each => each.index === index.index ).length > 0 )
        {
            // We already sent this query already
            return false;
        }
        else
        {
            // Otherwise add it
            state.window.pendingQueries.push( index );
            return true;
        }
    } );
    const added = results.includes( true );
    if ( added ) state.window.pendingQueries.sort( );
    return added;
}

export function forecastsAreEqual( a: ForecastTime | undefined, b: ForecastTime | undefined )
{
    if ( a === undefined && b === undefined ) return true;
    if ( a === undefined || b === undefined ) return false;
    return ( a.date === b.date && a.hour === b.hour );
}
/**
 * When we've either jumped outside of the cache window OR changed the surface/route/forecast,
 * we should clear the cache and pending queries
 * @param state the redux state - necessary to ensure redux can detect changes to the state
 * @param newForecastTime the new forecast time to display (if undefined, the taus will all have to reload)
 * @param includeCheck If true, it will skip clearing the cache if the forecast times are the same. If false,
 *  (e.g. if the surface name has changed) the cache will be cleared and all taus forced to be refreshed.
 */
export function clear( state: SurfaceState, newForecastTime: ForecastTime | undefined, includeCheck = true )
{
    if ( includeCheck && forecastsAreEqual( newForecastTime, state.window.forecastTime ) ) return;
    state.window.cachedEntries = [];
    state.window.pendingQueries = [];
    state.tausWithGeoJson = [];
    state.incompleteTauCount = 0;
    state.windDirectionWindow.taus = [];
    state.windDirectionWindow.forecastTime = undefined;
    state.shouldStopFetchingData = false;
    state.incompleteTauLatestError = undefined;
    state.window.forecastTime = newForecastTime;
}

/**
 * Get the index into the list of taus for which s3 has geoJson files that corresponds to the currently selected time
 * @param selectedTime the time currently selected on the time bar
 * @param tausWithGeoJson the list of taus for which there are geoJson existing in s3 for this forecast
 */
export function getCurrentGeoJsonIndex( selectedTime: number, tausWithGeoJson: Array<number> )
{
    const selectedTau = snapInterpolation( tausWithGeoJson, selectedTime );
    return tausWithGeoJson.indexOf( selectedTau );
}
/**
 * Get the index into the list of taus for which NOAA had a forecast tau that corresponds to the currently selected time
 * @param selectedTime the time currently selected on the time bar
 * @param tausWithGeoJson the list of taus for which there are geoJson existing in s3 for this forecast
 * @param allTaus the list of taus for which NOAA generated forecasts
 * @returns the index of the current time into the list of geojson taus, or -1 if we don't have a geojson for the current timestamp
 */
export function getCurrentOverallIndex( selectedTime: number, tausWithGeoJson: Array<number>, allTaus: Array<number> )
{
    const max = allTaus.length > 0 ? allTaus[allTaus.length - 1] : undefined;
    if ( max === undefined || selectedTime > max ) return -1; // if we're out of bounds of the forecast or we don't have the tau list yet, return "not found"
    const selectedTau = snapInterpolation( allTaus, selectedTime );
    return tausWithGeoJson.indexOf( selectedTau );
}

/**
 * Get the ForecastWindData object for the tau that matches the selected time (via snap interpolation),
 * if it exists.
 *
 * @param selectedTime the time currently selected on the time bar
 * @param allTaus the list of all NOAA-generated taus
 * @param data the array of ForecastWindData to search
 * @returns the matching ForecastWindData, or undefined if no match
 */
export function getForecastWindDataForSelectedTau(
    selectedTime: number,
    allTaus: Array<number>,
    data: Array<ForecastWindData>
): ForecastWindData | undefined
{
    if ( allTaus.length === 0 || selectedTime > allTaus[allTaus.length - 1] )
    {
        return undefined;
    }

    const selectedTau = snapInterpolation( allTaus, selectedTime );

    if ( !data.some( d => d.tau === selectedTau ) )
    {
        return undefined;
    }

    return data.find( d => d.tau === selectedTau );
}

